// src/components/admin/adminsidebar/AdminSideBar.jsx

import {
  Archive,
  Bell,
  ChevronDown,
  Gavel,
  HelpCircle,
  LayoutDashboard,
  MessageSquare,
  Package,
  Settings,
  Settings2,
  ShoppingCart,
  Tag,
  TicketPercent,
  Users,
} from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../../assets/css/admin/adminsidebar/AdminSideBar.css";
import AppContext from "../../../context/AppContext";

const AdminSidebar = ({ isCollapsed, isMobileOpen, onClose }) => {
  const { setAuthToken, loggedInAdmin } = useContext(AppContext); // 👈 loggedInAdmin add kiya
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // 👇 name se initials nikalne wala function (fallback "AD" agar naam na ho)
  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase() || "AD";

  const menuSections = [
    {
      section: "GENERAL",
      items: [
        {
          id: "dashboard",
          label: "Dashboard",
          icon: LayoutDashboard,
          path: "/admin/dashboard",
        },
      ],
    },
    {
      section: "MANAGEMENT",
      items: [
        {
          id: "categories",
          label: "Categories",
          icon: Tag,
          path: "/admin/category-list",
        },
        {
          id: "products",
          label: "Products",
          icon: Package,
          path: "/admin/product-list",
        },
        {
          id: "inventory",
          label: "Inventory & Stock",
          icon: Archive,
          path: "/admin/inventory/materials",
        },
        {
          id: "coupons",
          label: "Coupon Codes",
          icon: TicketPercent,
          path: "/admin/coupon-list",
        },
      ],
    },
    {
      section: "OPERATIONS",
      items: [
        {
          id: "orders",
          label: "Orders",
          icon: ShoppingCart,
          path: "/admin/order-list",
        },
        {
          id: "customization",
          label: "Custom Orders",
          icon: Settings2,
          path: "/admin/custom-orders",
        },
        {
          id: "bidding",
          label: "Bidding System",
          icon: Gavel,
          path: "/admin/bidding-list",
        },
        {
          id: "customers",
          label: "Customers",
          icon: Users,
          path: "/admin/customers",
        },
      ],
    },
    {
      section: "SYSTEM",
      items: [
        {
          id: "faq",
          label: "FAQ",
          icon: HelpCircle,
          path: "/admin/faq",
        },

        {
          id: "notifications",
          label: "Notifications",
          icon: Bell,
          path: "/admin/notifications",
        },
        {
          id: "help_support",
          label: "Help & Support",
          icon: MessageSquare,
          path: "/admin/help-support",
        },
        {
          id: "settings",
          label: "Settings",
          icon: Settings,
          path: "/admin/settings",
        },
      ],
    },
  ];

  useEffect(() => {
    setAuthToken(localStorage.getItem("authToken"));
    const currentPath = location.pathname;
    let foundActiveId = null;
    let foundParentId = null;

    menuSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.path === currentPath) foundActiveId = item.id;
        if (item.submenu) {
          item.submenu.forEach((sub) => {
            if (sub.path === currentPath) {
              foundActiveId = sub.id;
              foundParentId = item.id;
            }
          });
        }
      });
    });

    if (foundActiveId) {
      setActiveMenu(foundActiveId);
      if (foundParentId) setOpenSubmenu(foundParentId);
    }
  }, [location.pathname]);

  const toggleSubmenu = (menuId) => {
    setOpenSubmenu(openSubmenu === menuId ? null : menuId);
  };

  const handleMenuClick = (item) => {
    if (item.submenu) {
      toggleSubmenu(item.id);
    } else if (item.path) {
      setActiveMenu(item.id);
      setOpenSubmenu(null);
      navigate(item.path);
      if (window.innerWidth <= 768 && onClose) onClose();
    }
  };

  const handleSubmenuClick = (subItem) => {
    if (subItem.path) {
      setActiveMenu(subItem.id);
      navigate(subItem.path);
      if (window.innerWidth <= 768 && onClose) onClose();
    }
  };

  return (
    <>
      <div
        className={`ad-sidebar-overlay ${isMobileOpen ? "active" : ""}`}
        onClick={onClose}
      />

      <div
        className={`ad-sidebar-container
          ${isCollapsed ? "ad-sidebar-collapsed" : "ad-sidebar-expanded"}
          ${isMobileOpen ? "ad-sidebar-mobile-open" : ""}
        `}
      >
        <div className="ad-sidebar-header">
          {!isCollapsed && (
            <div className="ad-sidebar-logo">
              <h1 className="ad-sidebar-logo-title">AD Furniture</h1>
              <p className="ad-sidebar-logo-subtitle">Admin Panel</p>
            </div>
          )}
        </div>

        <nav className="ad-sidebar-nav">
          {menuSections.map((section, idx) => (
            <div key={idx} className="ad-sidebar-menu-section">
              {!isCollapsed && (
                <div className="ad-sidebar-section-header">
                  <p className="ad-sidebar-section-title">{section.section}</p>
                </div>
              )}

              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeMenu === item.id;
                const isSubmenuOpen = openSubmenu === item.id;
                const hasSubmenu = item.submenu && item.submenu.length > 0;

                return (
                  <div key={item.id} className="ad-sidebar-menu-item">
                    <button
                      onClick={() => handleMenuClick(item)}
                      className={`ad-sidebar-menu-btn ${isActive ? "ad-sidebar-active" : ""}`}
                      title={isCollapsed ? item.label : ""}
                    >
                      <div className="ad-sidebar-menu-content">
                        <Icon size={20} className="ad-sidebar-menu-icon" />
                        {!isCollapsed && (
                          <span className="ad-sidebar-menu-label">
                            {item.label}
                          </span>
                        )}
                      </div>
                      {!isCollapsed && hasSubmenu && (
                        <ChevronDown
                          size={16}
                          className={`ad-sidebar-chevron ${isSubmenuOpen ? "ad-sidebar-open" : ""}`}
                        />
                      )}
                    </button>

                    {hasSubmenu && !isCollapsed && (
                      <div
                        className={`ad-sidebar-submenu ${isSubmenuOpen ? "ad-sidebar-open" : "ad-sidebar-closed"}`}
                      >
                        {item.submenu.map((sub) => (
                          <button
                            key={sub.id}
                            onClick={() => handleSubmenuClick(sub)}
                            className={`ad-sidebar-submenu-btn ${activeMenu === sub.id ? "ad-sidebar-active" : ""}`}
                          >
                            <span className="ad-sidebar-submenu-dot" />
                            <span>{sub.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="ad-sidebar-user-profile">
          {!isCollapsed ? (
            <div className="ad-sidebar-profile-card">
              <div className="ad-sidebar-avatar">
                {getInitials(loggedInAdmin?.name)}
              </div>
              <div className="ad-sidebar-profile-info">
                <p className="ad-sidebar-profile-name">
                  {loggedInAdmin?.name || "Admin"}
                </p>
                <p className="ad-sidebar-profile-role">Administrator</p>
              </div>
            </div>
          ) : (
            <div
              className="ad-sidebar-avatar"
              title={loggedInAdmin?.name || "Admin"}
            >
              {getInitials(loggedInAdmin?.name)}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;